import type { ReactElement } from 'react'

import { isNil } from 'ramda'

import { FormattedCurrencyNumber } from '@twisto/components/atoms/format'
import type { TypographyProps } from '@twisto/components/atoms/typography'
import { Typography } from '@twisto/components/atoms/typography'

import * as styles from './amount.styles'

type Size = 'big' | 'medium' | 'small'

type Props = {
  amount: string | number
  currency?: string
  title: ReactElement
  subtitle?: ReactElement
  className?: string
  size?: Size
}

const amountVariants: Record<Size, TypographyProps['variant']> = {
  big: 'h1',
  medium: 'h2',
  small: 'h3',
}

const labelVariants: Record<Size, TypographyProps['variant']> = {
  big: 'b3',
  medium: 'b4',
  small: 'b4',
}

export const Amount = ({
  amount,
  currency,
  title,
  subtitle,
  size = 'big',
  className,
}: Props) => (
  <div className={className} css={styles.root} data-testid="amount">
    <Typography
      color="textSecondary"
      css={styles.label}
      fontWeight="medium"
      textTransform="uppercase"
      variant={labelVariants[size]}
    >
      {title}
    </Typography>
    <Typography
      component="div"
      css={styles.amount}
      data-testid="amount"
      fontWeight="heavy"
      variant={amountVariants[size]}
    >
      <FormattedCurrencyNumber currency={currency} value={amount} />
    </Typography>
    {!isNil(subtitle) && (
      <Typography color="textTertiary" component="div" variant="b2">
        {subtitle}
      </Typography>
    )}
  </div>
)
